import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Card, Button, Badge, Accordion, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { BookStatusBadge } from '../components/BookProgressBar';
import { getNativeLanguageName } from '../constants/languageNames';
import { getImageFrameStyle, getImageTagStyle } from '../constants/imageDisplay';

// Page labels for the image grid
const PAGE_LABELS = {
    '0': 'Front Cover',
    '13': 'Back Cover',
};
const getPageLabel = (key) => PAGE_LABELS[key] || `Page ${key}`;

// Ordered image keys: 0, 1-12, 13
const sortedImageKeys = (images) => {
    if (!images) return [];
    return Object.keys(images).sort((a, b) => parseInt(a) - parseInt(b));
};

function JobInfoRow({ label, value }) {
    return (
        <tr>
            <td className="text-muted" style={{ width: '35%', whiteSpace: 'nowrap' }}><strong>{label}</strong></td>
            <td>{value || <span className="text-muted">—</span>}</td>
        </tr>
    );
}

function AdminGenaiJobDetailScreen() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const [job, setJob] = useState(null);
    const [storyPages, setStoryPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [storyLoading, setStoryLoading] = useState(false);
    const [storyError, setStoryError] = useState(null);
    const [imageErrors, setImageErrors] = useState({});
    const [printSending, setPrintSending] = useState(false);

    useEffect(() => {
        if (!userInfo || !userInfo.isAdmin) {
            navigate('/login');
            return;
        }
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };

        axios.get(`/api/genai/admin/jobs/${jobId}/`, config)
            .then(res => { setJob(res.data); setLoading(false); })
            .catch(err => {
                setError(err.response?.data?.error || err.message);
                setLoading(false);
            });
    }, [jobId, navigate, userInfo]);

    const loadStory = () => {
        if (storyPages.length > 0) return; // already loaded
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        setStoryLoading(true);
        axios.get(`/api/genai/admin/jobs/${jobId}/story/`, config)
            .then(res => { setStoryPages(res.data.pages || []); setStoryLoading(false); })
            .catch(err => { setStoryError(err.response?.data?.error || err.message); setStoryLoading(false); });
    };

    const handleImageError = (key) => {
        setImageErrors(prev => ({ ...prev, [key]: true }));
    };

    const handleResubmitToPrinting = async () => {
        if (!job) return;
        setPrintSending(true);
        try {
            const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
            const { data } = await axios.post(
                `/api/genai/jobs/${job.job_id}/send-to-printing/`,
                { resubmit: true },
                config
            );
            alert(`✓ ${data.message}\nOrder ID: ${data.order_id}\nCopies: ${data.copies}\nEstimated Delivery: ${data.estimated_delivery}`);

            const refreshed = await axios.get(`/api/genai/admin/jobs/${jobId}/`, config);
            setJob(refreshed.data);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Failed to resubmit printing';
            alert(msg);
        } finally {
            setPrintSending(false);
        }
    };

    const handleDownloadPrintPDF = async () => {
        if (!job || !userInfo?.token) return;
        try {
            const response = await fetch(`/api/genai/admin/jobs/${job.job_id}/download-print-pdf/`, {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`,
                },
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                alert(data.error || 'Failed to download print PDF');
                return;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
            link.download = filenameMatch ? filenameMatch[1] : `book_${job.job_id}_print.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert(err?.message || 'Failed to download print PDF');
        }
    };

    if (loading) return <Loader />;
    if (error) return <Message variant="danger">{error}</Message>;
    if (!job) return null;

    const input = job.input_data || {};
    const characters = input.characters || [];
    const imageKeys = sortedImageKeys(job.images);

    const formatDate = (dt) => dt
        ? new Date(dt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : '—';

    return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={() => navigate('/admin/genai/jobs')}>
                ← Back to Job List
            </Button>

            <h2 className="mb-1">Job Detail</h2>
            <code className="text-muted small">{job.job_id}</code>

            {/* ── Status row ── */}
            <div className="mt-2 mb-4 d-flex align-items-center gap-3">
                <BookStatusBadge status={job.status} currentStep={job.current_step} />
                {job.current_step && (
                    <Badge bg="secondary">{job.current_step}</Badge>
                )}
            </div>

            <Row>
                {/* ── Left column: job info + input data ── */}
                <Col md={5}>
                    <Card className="mb-4">
                        <Card.Header><strong>Job Info</strong></Card.Header>
                        <Card.Body className="p-0">
                            <table className="table table-sm mb-0">
                                <tbody>
                                    <JobInfoRow label="User" value={`${job.user?.email || job.user?.username || '—'} (id: ${job.user?.id})`} />
                                    <JobInfoRow label="Order ID" value={job.order_id} />
                                    <JobInfoRow label="Language" value={getNativeLanguageName(job.language || input.language || 'English', 'English')} />
                                    <JobInfoRow label="Created" value={formatDate(job.created_at)} />
                                    <JobInfoRow label="Updated" value={formatDate(job.updated_at)} />
                                    <JobInfoRow label="Completed" value={formatDate(job.completed_at)} />
                                    <JobInfoRow label="Retries" value={job.retry_count ?? 0} />
                                    <JobInfoRow label="Regenerations" value={job.user_regeneration_count ?? 0} />
                                    {job.pdf_url && (
                                        <tr>
                                            <td className="text-muted"><strong>Print PDF</strong></td>
                                            <td>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={handleDownloadPrintPDF}
                                                >
                                                    Download Print PDF
                                                </Button>
                                            </td>
                                        </tr>
                                    )}
                                    {job.error_message && (
                                        <tr>
                                            <td className="text-muted"><strong>Error</strong></td>
                                            <td className="text-danger small">{job.error_message}</td>
                                        </tr>
                                    )}
                                    {job.print_submitted && (
                                        <tr>
                                            <td className="text-muted"><strong>Print</strong></td>
                                            <td>
                                                <div className="d-flex flex-column gap-2 align-items-start">
                                                    <div className="small text-muted">
                                                        Submitted{job.print_provider_order_id ? ` (provider: ${job.print_provider_order_id})` : ''}
                                                    </div>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        onClick={handleResubmitToPrinting}
                                                        disabled={printSending || job.status !== 'completed'}
                                                    >
                                                        {printSending ? 'Resubmitting...' : '↻ Resubmit to Printing'}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Header><strong>Book Input</strong></Card.Header>
                        <Card.Body>
                            {characters.length > 0 ? (
                                characters.map((char, idx) => (
                                    <div key={idx} className="mb-3 p-2 border rounded">
                                        <div><strong>{char.name}</strong> — {char.type}{char.subtype ? ` (${char.subtype})` : ''}</div>
                                        {char.gender && <div className="small text-muted">Gender: {char.gender}</div>}
                                        {char.age && <div className="small text-muted">Age: {char.age}</div>}
                                        {char.freetext && <div className="small text-muted mt-1">{char.freetext}</div>}
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted">No character data.</p>
                            )}
                            {input.story_locations && (
                                <div className="mt-2">
                                    <strong>Locations:</strong>
                                    <p className="small text-muted mb-0">{input.story_locations}</p>
                                </div>
                            )}
                            {input.story_freetext && (
                                <div className="mt-2">
                                    <strong>Story notes:</strong>
                                    <p className="small text-muted mb-0">{input.story_freetext}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Story content (lazy load) */}
                    <Card className="mb-4">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <strong>Story Pages</strong>
                            <Button variant="outline-secondary" size="sm" onClick={loadStory} disabled={storyLoading}>
                                {storyLoading ? <Spinner animation="border" size="sm" /> : storyPages.length > 0 ? 'Reload' : 'Load Story'}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {storyError && <div className="p-3"><Message variant="danger">{storyError}</Message></div>}
                            {storyPages.length > 0 && (
                                <Accordion>
                                    {storyPages.map(page => (
                                        <Accordion.Item key={page.page_number} eventKey={String(page.page_number)}>
                                            <Accordion.Header>
                                                {page.page_number === 0 ? 'Front Cover' : page.page_number === 13 ? 'Back Cover' : `Page ${page.page_number}`}
                                            </Accordion.Header>
                                            <Accordion.Body className="small">
                                                {page.picture && (
                                                    <div className="mb-2">
                                                        <strong>Picture:</strong>
                                                        <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>{page.picture}</p>
                                                    </div>
                                                )}
                                                {page.text && (
                                                    <div>
                                                        <strong>Text:</strong>
                                                        <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{page.text}</p>
                                                    </div>
                                                )}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            )}
                            {storyPages.length === 0 && !storyLoading && !storyError && (
                                <p className="text-muted p-3 mb-0">Click "Load Story" to view page text.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* ── Right column: images ── */}
                <Col md={7}>
                    <Card>
                        <Card.Header><strong>Generated Images</strong> <span className="text-muted small">({imageKeys.length} / 14)</span></Card.Header>
                        <Card.Body>
                            {imageKeys.length === 0 ? (
                                <p className="text-muted">No images generated yet.</p>
                            ) : (
                                <Row xs={2} md={3} className="g-3">
                                    {imageKeys.map(key => {
                                        const imgData = job.images[key];
                                        const url = imgData?.url || imgData;
                                        const label = getPageLabel(key);
                                        return (
                                            <Col key={key}>
                                                <Card className="h-100">
                                                    <div style={{ background: '#f8f9fa', minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {imageErrors[key] ? (
                                                            <span className="text-muted small">Image unavailable</span>
                                                        ) : (
                                                            <div style={getImageFrameStyle(imgData, key, { maxHeightCover: 180, maxHeightStory: 180 })}>
                                                                <Card.Img
                                                                    variant="top"
                                                                    src={url}
                                                                    alt={label}
                                                                    style={{ ...getImageTagStyle(), cursor: 'pointer' }}
                                                                    onClick={() => window.open(url, '_blank')}
                                                                    onError={() => handleImageError(key)}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Card.Footer className="text-center py-1">
                                                        <small className="text-muted">{label}</small>
                                                    </Card.Footer>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default AdminGenaiJobDetailScreen;
